/**
 * OrderTab — order tracking lookup UI for the right-panel "Order" tab.
 *
 * Iter 152.6 (Feb 2026, Yash) — extracted from ChatsPage.jsx as part of the
 * structural refactor. Pure presentational; the parent owns lookup state,
 * order data, and the auto-detected-orders list. The component renders an
 * order-lookup search input, an auto-detected-from-chat suggestion list,
 * and the populated order details (header card, items, courier+tracking).
 *
 * Note: `<WebsiteOrdersPanel/>` is rendered by the parent below this tab —
 * we keep it out of here to avoid passing the lead twice.
 */
import { Package, Search, Loader2, Sparkles, Truck, ExternalLink } from "lucide-react";
import { getCourierTrackingLink } from "../../lib/courierTracking";

const OrderTab = ({
  orderLookup,
  setOrderLookup,
  orderLoading,
  orderData,
  setOrderData,
  detectedOrders,
  onLookup,
}) => (
  <div className="p-4" data-testid="order-tracking-section">
    <div className="flex items-center gap-2 mb-2.5">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-sm">
        <Package size={13} />
      </div>
      <div>
        <p className="text-[12px] font-extrabold text-[--text-primary] leading-tight">Order / Tracking Lookup</p>
        <p className="text-[9px] text-[--text-muted] leading-tight">Order # or courier tracking #</p>
      </div>
    </div>

    <div className="flex gap-2 mb-3">
      <div className="flex-1 relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[--text-muted] pointer-events-none" />
        <input
          type="text"
          value={orderLookup}
          onChange={(e) => setOrderLookup(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onLookup(); }}
          placeholder="e.g. 2800273 or 21018570543"
          className="w-full pl-8 pr-3 py-2 border-2 border-[--border] rounded-xl text-xs outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all font-mono bg-white"
          data-testid="order-lookup-input"
        />
      </div>
      <button
        onClick={() => onLookup()}
        disabled={orderLoading || !orderLookup.trim()}
        className="px-3 py-2 rounded-xl text-[11px] font-extrabold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm flex items-center gap-1 transition-all"
        data-testid="order-lookup-btn"
      >
        {orderLoading ? <Loader2 size={12} className="animate-spin" /> : <><Search size={12} /> Search</>}
      </button>
    </div>

    {/* Auto-detected orders from chat */}
    {detectedOrders.length > 0 && !orderData && (
      <div className="space-y-2 mb-3">
        <p className="text-[9px] font-bold text-green-600 uppercase flex items-center gap-1">
          <Sparkles size={10} /> Auto-detected from chat
        </p>
        {detectedOrders.map((o) => (
          <button
            key={o.order_number}
            onClick={() => { setOrderLookup(String(o.order_number)); setOrderData(o); }}
            className="w-full text-left p-2 bg-green-50 border border-green-200 rounded-xl text-xs hover:bg-green-100 transition-all"
            data-testid={`detected-order-${o.order_number}`}
          >
            <span className="font-bold text-green-700">#{o.order_number}</span>
            <span className="text-green-600 ml-2">{o.items?.length || 0} item(s) · {o.order_date}</span>
          </button>
        ))}
      </div>
    )}

    {/* Order details */}
    {orderData && orderData.items && (
      <div className="space-y-3 animate-fade-up" data-testid="order-details">
        {/* Header card — order #, formatted date on the right, total chip */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-50 via-white to-blue-50/40 border border-slate-200 px-3.5 py-3 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.14em]">Order</p>
              <p className="text-base font-extrabold text-slate-900 leading-tight font-mono" data-testid="order-number-heading">
                #{orderData.order_number}
              </p>
              {orderData.items?.[0]?.Payment_Type && (
                <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200" data-testid="order-payment-type">
                  {String(orderData.items[0].Payment_Type).toUpperCase()}
                </span>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.14em]">Order Date</p>
              <p className="text-[12px] font-bold text-slate-800 leading-tight" data-testid="order-date">
                {(() => {
                  // Upstream sends DD-MM-YYYY. Render as "DD MMM YYYY".
                  const raw = orderData.order_date || "";
                  const m = String(raw).match(/^(\d{2})-(\d{2})-(\d{4})$/);
                  if (m) {
                    const dt = new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`);
                    if (!isNaN(dt)) {
                      return dt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
                    }
                  }
                  return raw;
                })()}
              </p>
            </div>
          </div>
          {/* Order-level total — premium amount card */}
          {(orderData.order_amount || orderData.Currency) && (
            <div className="mt-3 flex items-center justify-between rounded-xl bg-gradient-to-r from-amber-100/70 via-yellow-50 to-orange-50 border border-amber-200/80 px-3 py-2" data-testid="order-amount-row">
              <span className="text-[10px] font-bold text-amber-800 uppercase tracking-[0.14em]">Order Total</span>
              <span className="text-base font-extrabold text-amber-900" data-testid="order-amount-value" style={{ fontFamily: "Outfit" }}>
                {orderData.Currency === "USD" ? "$" : orderData.Currency === "EUR" ? "€" : orderData.Currency === "GBP" ? "£" : "₹"}
                {Number(orderData.order_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                {orderData.Currency && orderData.Currency !== "INR" && (
                  <span className="ml-1 text-[10px] font-bold text-amber-700">{orderData.Currency}</span>
                )}
              </span>
            </div>
          )}
        </div>

        {orderData.items.map((item, idx) => {
          const trackLink = item.tracking_number ? getCourierTrackingLink(item.courier, item.tracking_number) : null;
          const isShipped = item.status?.toLowerCase() === "shipped";
          const isDelivered = item.status?.toLowerCase() === "delivered";
          const shipCountry = item.Shipping_Country || item.shipping_country;
          const billCountry = item.Billing_Country || item.billing_country;
          const itemImage = item.Image || item.image;
          return (
            <div key={item.item_code || `item-${idx}`} className={`rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow ${item.is_overdue && !isShipped && !isDelivered ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`} data-testid={`order-item-${idx}`}>
              <div className="p-3 flex gap-3">
                {/* Product image — clickable, opens full image. Auto-hides on broken URL. */}
                {itemImage && (
                  <a
                    href={itemImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 block w-16 h-16 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-blue-300 transition-all"
                    data-testid={`order-item-image-link-${idx}`}
                  >
                    <img
                      src={itemImage}
                      alt={item.product_title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                      data-testid={`order-item-image-${idx}`}
                    />
                  </a>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-900 leading-snug line-clamp-2" style={{ fontFamily: "Outfit" }}>{item.product_title}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <span className="text-[9px] font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{item.item_code}</span>
                    {item.category && (
                      <span className="text-[9px] font-bold text-violet-700 bg-violet-50 border border-violet-100 px-1.5 py-0.5 rounded uppercase tracking-wider">{item.category}</span>
                    )}
                    {item.leadtime_days && (
                      <span className="text-[9px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">⏱ {item.leadtime_days}d</span>
                    )}
                  </div>
                  {/* Item price + status */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-extrabold text-[--accent]" style={{ fontFamily: "Outfit" }}>
                      ₹{Number(item.price || 0).toLocaleString()}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      isShipped ? "bg-green-100 text-green-700 border border-green-200" :
                      isDelivered ? "bg-blue-100 text-blue-700 border border-blue-200" :
                      item.is_overdue ? "bg-red-100 text-red-700 border border-red-200" :
                      "bg-yellow-100 text-yellow-800 border border-yellow-200"
                    }`} data-testid={`order-status-${idx}`}>
                      {item.is_overdue && !isShipped && !isDelivered ? "OVERDUE" : item.status || "Processing"}
                    </span>
                  </div>
                  {/* Country + shipped meta */}
                  {(shipCountry || billCountry || item.shipped_date) && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] mt-1.5 pt-1.5 border-t border-dashed border-slate-200" data-testid={`order-countries-${idx}`}>
                      {shipCountry && (
                        <span className="text-slate-500">🚚 <span className="text-slate-800 font-bold">{shipCountry}</span></span>
                      )}
                      {billCountry && billCountry !== shipCountry && (
                        <span className="text-slate-500">💳 <span className="text-slate-800 font-bold">{billCountry}</span></span>
                      )}
                      {item.shipped_date && (
                        <span className="text-slate-500 ml-auto">Shipped {new Date(item.shipped_date).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* Courier + tracking footer */}
              {(item.courier || item.tracking_number) && (
                <div className="px-3 py-2 bg-gradient-to-r from-blue-50/60 to-indigo-50/40 border-t border-blue-100 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {item.courier && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-900">
                      <Truck size={10} /> {item.courier}
                    </span>
                  )}
                  {item.tracking_number && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-900/90">
                      <span className="font-mono select-all bg-white border border-blue-200 px-1.5 py-[1px] rounded">{item.tracking_number}</span>
                    </span>
                  )}
                  {trackLink && (
                    <a
                      href={trackLink.url}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto inline-flex items-center gap-1 text-[10px] font-extrabold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 px-2 py-1 rounded-full shadow-sm transition-all"
                      data-testid={`track-courier-btn-${idx}`}
                      title={`Open tracking on ${trackLink.label}`}
                    >
                      Track on {trackLink.label}
                      <ExternalLink size={9} />
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <button
          onClick={() => setOrderData(null)}
          className="text-[10px] text-[--text-muted] hover:text-[--accent] font-bold"
          data-testid="clear-order-btn"
        >
          Clear
        </button>
      </div>
    )}

    {!orderData && detectedOrders.length === 0 && (
      <p className="text-[10px] text-[--text-muted] text-center py-6">
        Enter an order or courier tracking number to look up shipment status.
      </p>
    )}
  </div>
);

export default OrderTab;
